import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { INITIAL_PAGINATION_INFO } from '@routes/profile-bar/___pages___/my-results/___shared___/constants.ts';
import type {
    LearningFiledsFiltersValues,
    ResultsRequest,
    UserAssessment,
    UserLearning,
} from '@routes/profile-bar/___pages___/my-results/api/results.api.ts';
import {
    getMyAssessmentResults,
    getMyColumnsFiltersValues,
    getMyLearningResults,
} from '@routes/profile-bar/___pages___/my-results/api/results.api.ts';
import { MY_LEARNING_DATA_INDEX } from '@routes/profile-bar/___pages___/my-results/ui/tables/my-learning/constants.ts';
import type { CommonSearchRequest, EmptyResultsDescription, PaginatedResponse } from '@types';
import { extractError } from '@utils/slice.ts';

interface ResultsSlice {
    error: Record<string, any> | null;
    myLearningResults: PaginatedResponse<UserLearning, EmptyResultsDescription>;
    myAssessmentResults: PaginatedResponse<UserAssessment, EmptyResultsDescription>;
    myColumnsFiltersValues: LearningFiledsFiltersValues;
    myLearningRequestData: ResultsRequest;
    myAssessmentRequestData: ResultsRequest;

    isGetLearningResultsPending: boolean;
    isGetAssessmentResultsPending: boolean;
    isGetColumnsFiltersValuesPending: boolean;
}

const INITIAL_REQUEST: CommonSearchRequest = {
    textSearch: '',
    filters: null,
    pageNumber: 1,
    pageSize: 10,
    sortBy: MY_LEARNING_DATA_INDEX.DATE, // Results are always sorted by the most recently completed or updated items
    sortOrder: 'descending',
};

const INITIAL_FIELDS_FILTERS_DATA = {
    ranks: [],
    locations: [],
    statuses: [],
    methods: [],
};

const INITIAL_LEARNING_TABLE_DATA: PaginatedResponse<UserLearning, EmptyResultsDescription> = {
    data: [],
    pagination: INITIAL_PAGINATION_INFO,
    resultsDescription: null,
};
const INITIAL_ASSESMENT_TABLE_DATA: PaginatedResponse<UserAssessment, EmptyResultsDescription> = {
    data: [],
    pagination: INITIAL_PAGINATION_INFO,
    resultsDescription: null,
};

const initialState: ResultsSlice = {
    error: null,
    myLearningResults: INITIAL_LEARNING_TABLE_DATA,
    myAssessmentResults: INITIAL_ASSESMENT_TABLE_DATA,
    myLearningRequestData: INITIAL_REQUEST,
    myAssessmentRequestData: INITIAL_REQUEST,
    myColumnsFiltersValues: INITIAL_FIELDS_FILTERS_DATA,

    isGetLearningResultsPending: false,
    isGetAssessmentResultsPending: false,
    isGetColumnsFiltersValuesPending: false,
};

export const getColumnsFiltersValues = createAsyncThunk<LearningFiledsFiltersValues, void, { rejectValue: any }>(
    'results/fetchFilters',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getMyColumnsFiltersValues();
            return res;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const getLearningResults = createAsyncThunk<
    PaginatedResponse<UserLearning, EmptyResultsDescription>,
    ResultsRequest,
    { rejectValue: any }
>('results/learningResults', (payload, { rejectWithValue }) =>
    getMyLearningResults(payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getAssessmentResults = createAsyncThunk<
    PaginatedResponse<UserAssessment, EmptyResultsDescription>,
    ResultsRequest,
    { rejectValue: any }
>('results/assessmentResults', (payload, { rejectWithValue }) =>
    getMyAssessmentResults(payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const resultsSlice = createSlice({
    name: 'results',
    initialState,
    reducers: {
        updateLearningRequestData: (state, action: PayloadAction<ResultsRequest>) => {
            state.myLearningRequestData = action.payload;
        },
        updateAssessmentRequestData: (state, action: PayloadAction<ResultsRequest>) => {
            state.myAssessmentRequestData = action.payload;
        },
        clearResultsState: () => ({ ...initialState }),
    },
    extraReducers: (builder) => {
        builder.addCase(getLearningResults.pending, (state) => {
            state.error = null;
            state.isGetLearningResultsPending = true;
        });
        builder.addCase(getLearningResults.fulfilled, (state, action) => {
            state.myLearningResults = action.payload;
            state.isGetLearningResultsPending = false;
        });
        builder.addCase(getLearningResults.rejected, (state, action) => {
            state.error = extractError(action);
            state.isGetLearningResultsPending = false;
        });

        builder.addCase(getAssessmentResults.pending, (state) => {
            state.error = null;
            state.isGetAssessmentResultsPending = true;
        });
        builder.addCase(getAssessmentResults.fulfilled, (state, action) => {
            state.myAssessmentResults = action.payload;
            state.isGetAssessmentResultsPending = false;
        });
        builder.addCase(getAssessmentResults.rejected, (state, action) => {
            state.error = extractError(action);
            state.isGetAssessmentResultsPending = false;
        });

        builder.addCase(getColumnsFiltersValues.pending, (state) => {
            state.error = null;
            state.isGetColumnsFiltersValuesPending = true;
        });
        builder.addCase(getColumnsFiltersValues.fulfilled, (state, action) => {
            state.myColumnsFiltersValues = action.payload;
            state.isGetColumnsFiltersValuesPending = false;
        });
        builder.addCase(getColumnsFiltersValues.rejected, (state, action) => {
            state.error = extractError(action);
            state.isGetColumnsFiltersValuesPending = false;
        });
    },
});

export const { updateAssessmentRequestData, updateLearningRequestData, clearResultsState } = resultsSlice.actions;

export default resultsSlice.reducer;
